<template>
    <div class="leases-tab">
        <!-- Header -->
        <div class="tab-header">
            <div class="tab-header-left">
                <h3 class="tab-title">Leases</h3>
            </div>
            <themed-button 
                @click="openManageLeases" 
                variant="default"
            >
                Manage Leases
            </themed-button>
        </div>

        <!-- Leases and Units Side by Side -->
        <div class="leases-tab__grid">
            <!-- Left - Leases Table -->
            <div class="leases-tab__section">
                <h4 class="leases-tab__section-title">Leases</h4>
                <themed-table
                    :data="sites.selectedSite.leases"
                    :columns="leasesColumns"
                    :hoverable="true"
                    wrapper-class="your-border-class"
                    aria-label="Leases table"
                    @row-click="handleLeaseClick"
                />
            </div>

            <!-- Right - Units Table -->
            <div v-if="selectedLeaseId" class="leases-tab__section">
                <h4 class="leases-tab__section-title">
                    Units for <span class="leases-tab__section-title--highlight">{{ leases.selectedLease ? leases.selectedLease.name : '' }}</span>
                </h4>
                <themed-table
                    :data="leases.selectedLease?.units || []"
                    :columns="unitsColumns"
                    :hoverable="true"
                    wrapper-class="your-border-class"
                    aria-label="Units table"
                />
            </div>
        </div>

        <!-- Manage Leases Modal -->
        <notifications classes="notification" position="bottom right" />
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        name: 'ThemedLeases',

        props: {
            data: {
                type: [Array, Object],
                required: true
            }
        },
        
        data() {
            return {
                selectedLeaseId: this.data.leases[0] ? this.data.leases[0].id : ''
            }
        },

        created() {
            this.leases.all = this.data.leases;
            this.leases.filtered = this.data.leases;
            this.leases.selectedLease = _.find(this.data.leases, ['id', this.selectedLeaseId]);
            this.sites.selectedSite = this.data;
        },

        watch: {
            selectedLeaseId() {
                this.leases.selectedLease = _.find(this.sites.selectedSite.leases, ['id', this.selectedLeaseId]);
            }
        },

        computed: {
            ...mapState(['leases']),
            ...mapState(['sites']),

            leasesColumns() {
                return [
                    { field: 'name', label: 'NAME', sortable: false},
                    { 
                        field: 'total_square_feet', 
                        label: 'SQ. FT.', 
                        align: 'right',
                        sortable: false,
                        width: '90px',
                        formatter: (value) => this.formatNumber(value)
                    },
                    { 
                        field: 'start_date', 
                        label: 'START', 
                        sortable: false,
                        width: '90px',
                        formatter: (value) => this.$options.filters.date(value)
                    },
                    { 
                        field: 'end_date', 
                        label: 'END', 
                        sortable: false,
                        width: '90px',
                        formatter: (value) => this.$options.filters.date(value)
                    },
                    { 
                        field: 'utilization_percentage', 
                        label: 'UTIL %', 
                        align: 'right',
                        sortable: false,
                        width: '50px',
                        formatter: (value, row) => {
                            const util = row.utilization_percentage || this.calculateUtilization(row);
                            return util ? `${Math.round(util)}%` : '0%';
                        }
                    }
                ];
            },

            unitsColumns() {
                return [
                    { 
                        field: 'business_unit', 
                        label: 'BIZ UNIT', 
                        sortable: false,
                        width: '375px',
                        formatter: (value, row) => {
                            // Try to get business unit from various sources
                            if (row.business_unit) return row.business_unit;
                            if (row.subsidiary?.name) return row.subsidiary.name;
                            if (this.sites.selectedSite?.subsidiary?.name) return this.sites.selectedSite.subsidiary.name;
                            return '-';
                        }
                    },
                    { 
                        field: 'floor', 
                        label: 'FLR', 
                        sortable: false,
                        width: '40px'
                    },
                    { 
                        field: 'suite', 
                        label: 'STE', 
                        sortable: false,
                        width: '40px'
                    },
                    { 
                        field: 'pivot.leased_square_footage', 
                        label: 'SIZE', 
                        align: 'right',
                        sortable: false,
                        width: '100px',
                        formatter: (value, row) => this.formatNumber(row.pivot?.leased_square_footage)
                    },
                    { 
                        field: 'divisible', 
                        label: 'DIVISIBLE', 
                        align: 'right',
                        sortable: false,
                        width: '100px',
                        formatter: (value) => this.formatNumber(value)
                    },
                    { 
                        field: 'ti_allowance', 
                        label: 'TIA $', 
                        align: 'right',
                        sortable: false,
                        width: '75px',
                        formatter: (value) => this.formatCurrency(value, 2)
                    }
                ];
            }
        },

        methods: {
            openManageLeases() {
                this.$emit('open-manage-leases', 'lease-info');
            },

            handleLeaseClick(lease) {
                if (this.leases.all.length > 1) {
                    this.selectedLeaseId = lease.id;
                }
            },

            calculateUtilization(lease) {
                // Calculate utilization percentage based on leased square footage vs total available
                if (!lease.total_square_feet || lease.total_square_feet === 0) {
                    return 0;
                }
                
                // If units exist, calculate from units
                if (lease.units && lease.units.length > 0) {
                    const leasedSqFt = lease.units.reduce((sum, unit) => {
                        return sum + (unit.pivot?.leased_square_footage || 0);
                    }, 0);
                    return (leasedSqFt / lease.total_square_feet) * 100;
                }
                
                // Default to 100% if no units data
                return 100;
            }
        }
    }
</script>
